/**
 * @param {number} n
 * @returns {string}
 */
export function formatPoints(n) {
    const s = String(Math.trunc(Math.abs(n)));
    return s.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/**
 * 付款彈窗明細列：總額 / 點數 / 玩瘋券（後兩者皆帶剩餘餘額）；付現時多出「折抵合計」
 * 與「現金」。全額由點數折抵時不列合計——它只是上兩列的加總，與總計區重複。
 * 「總額」是扣抵前的金額（= 點數 + 玩瘋券 + 現金）；「剩餘」是這筆扣抵**後**的餘額，
 * 與同一列右側的扣抵金額對得起來。轉蛋券列例外：顯示的是扣抵**前**持有的張數
 * （`coupon_balance` 原值，帶負號、不加千分位，與「我的帳戶 > 轉蛋券」頁字面一致），
 * 因此不用「剩餘」。
 *
 * `toggle` 指出這列對應哪個折抵旗標（沒有就是 null，該列不放 checkbox，渲染成單行
 * 資訊列）；`checked` 取自後端回傳的 `discount_flags`，缺席時視為全開，與後端的相容
 * 規則同向。
 *
 * `balanceText` 是折抵列第二行的餘額，獨立於 `label`，讓渲染端排成名稱／餘額兩行；
 * 資訊列沒有第二行，恆為空字串。
 *
 * 轉蛋券（`coupon_deducted`）沒有 checkbox，有餘額就一定先折抵，明細列固定排在總額
 * 之下、點數之上。它是資訊列，持有張數跟著寫在標題裡。一番賞的報價沒有這個欄位。
 *
 * 商品禁用的折抵（`discount_allowed` 該欄為 false）整列不畫，連 checkbox 一起拿掉；
 * 點數、玩瘋券都被禁用時「折抵合計」也不列（見 {@link allowsPointDiscounts}）。
 * `discount_allowed` 缺席、或其中欄位缺席，都視為允許。
 * @param {{total_price: number, wc_usage: number, rp_usage: number, cash_amount: number, wc_balance: number, rp_balance: number, coupon_deducted?: number, coupon_balance?: number, discount_flags?: {points?: boolean, return_points?: boolean}, discount_allowed?: {points?: boolean, return_points?: boolean, coupons?: boolean}}} quote
 * @returns {Array<{label: string, balanceText: string, value: string, toggle: ?string, checked: boolean}>}
 */
export function quoteLines(quote) {
    const flags = quote.discount_flags || {};
    const allowed = quote.discount_allowed || {};
    const infoLine = (label, value) => ({ label, balanceText: '', value, toggle: null, checked: false });
    const lines = [
        infoLine('總額', 'NT$' + formatPoints(quote.total_price)),
    ];

    if (quote.coupon_deducted !== undefined && allowed.coupons !== false) {
        lines.push(infoLine(
            '使用轉蛋券抵扣（擁有:' + String(quote.coupon_balance) + ' 張）',
            '-NT$' + formatPoints(quote.coupon_deducted),
        ));
    }

    if (allowed.points !== false) {
        lines.push({
            label: '使用點數抵扣',
            balanceText: '剩餘 ' + formatPoints(quote.wc_balance - quote.wc_usage) + ' 點數',
            value: '-NT$' + formatPoints(quote.wc_usage),
            toggle: 'points',
            checked: flags.points !== false,
        });
    }

    if (allowed.return_points !== false) {
        lines.push({
            label: '使用玩瘋券抵扣',
            balanceText: '剩餘 ' + formatPoints(quote.rp_balance - quote.rp_usage) + ' 玩瘋券',
            value: '-NT$' + formatPoints(quote.rp_usage),
            toggle: 'return_points',
            checked: flags.return_points !== false,
        });
    }

    if (quote.cash_amount > 0) {
        if (allowsPointDiscounts(quote)) {
            lines.push(infoLine('折抵合計', 'NT$' + formatPoints(quote.wc_usage + quote.rp_usage)));
        }
        lines.push(infoLine('現金', 'NT$' + formatPoints(quote.cash_amount)));
    }

    return lines;
}

/**
 * 點數、玩瘋券至少一項允許折抵。兩項都被商品禁用時，「折抵合計」列與換算說明都不顯示。
 * `discount_allowed` 缺席、或其中欄位缺席，都視為允許。
 * @param {{discount_allowed?: {points?: boolean, return_points?: boolean}}} quote
 * @returns {boolean}
 */
export function allowsPointDiscounts(quote) {
    const allowed = quote.discount_allowed || {};
    return allowed.points !== false || allowed.return_points !== false;
}

/**
 * 總計區「實際支付」的金額：使用者要付出去的現金。與確認鈕同一來源，兩處數字才不會對不起來。
 * @param {{cash_amount: number}} quote
 * @returns {string}
 */
export function payableAmount(quote) {
    return 'NT$' + formatPoints(quote.cash_amount);
}

/**
 * 總計區要不要出現「實際支付」那列：沒有現金差額就不提支付。付現路徑未開放不影響——
 * 該擋的是確認鈕，不是金額。
 * @param {{cash_amount: number}} quote
 * @returns {boolean}
 */
export function shouldShowPayableAmount(quote) {
    return quote.cash_amount > 0;
}

export const DRAW_BUTTON_LABELS = {
    pointsOnly: '確認抽獎',
    blocked: '目前未開放付現',
    payPrefix: '確認支付NT$',
    paySuffix: '',
};

/**
 * @param {{cash_amount: number, can_pay_cash: boolean}} quote
 * @param {boolean} agreed 是否已勾選購買須知同意
 * @param {{pointsOnly: string, blocked: string, payPrefix: string, paySuffix: string}} labels
 * @returns {{label: string, disabled: boolean}}
 */
export function drawButtonState(quote, agreed, labels = DRAW_BUTTON_LABELS) {
    const state = (() => {
        if (quote.cash_amount === 0) {
            return { label: labels.pointsOnly, disabled: false };
        }
        if (quote.can_pay_cash) {
            return { label: labels.payPrefix + formatPoints(quote.cash_amount) + labels.paySuffix, disabled: false };
        }
        return { label: labels.blocked, disabled: true };
    })();

    if (!agreed) {
        return { ...state, disabled: true };
    }
    return state;
}

/**
 * 付款彈窗是否顯示付款方式 / 發票區塊：現金差額大於 0 且付現路徑已開放。
 * @param {{cash_amount: number, can_pay_cash: boolean}} quote
 * @returns {boolean}
 */
export function shouldShowPaymentSection(quote) {
    return quote.cash_amount > 0 && quote.can_pay_cash === true;
}

/**
 * 該發票型別要顯示的欄位名（供前端決定哪些輸入框要出現）。
 * @param {string} type
 * @returns {string[]}
 */
export function invoiceFieldsFor(type) {
    switch (type) {
        case 'company':
            return ['tax_number', 'title'];
        case 'phone_carruer':
        case 'id_carruer':
            return ['buyer_name', 'carruer_num'];
        case 'donation':
            return ['buyer_name', 'lovecode'];
        case 'individual':
            return ['buyer_name'];
        default:
            return [];
    }
}
